/**
 * extractSearchKeywords — HTML → searchable keyword array.
 *
 * Replaces v1's Lunr-based `extractLunrKeywords()` with a zero-dependency
 * regex tokenizer. No DOM required (no `document.createElement`).
 *
 * Output: `string[]` of lowercase, deduplicated words (≥2 chars).
 * Stored in `fullTextIndex` field, indexed by Dexie's multi-entry index (`*fullTextIndex`).
 *
 * Search queries use: `db.totodo.where("fullTextIndex").startsWithAnyOf(terms)`
 *
 * Performance: ~0.02ms per document. No external library.
 * Bundle impact: 0 bytes (replaces Lunr's ~15KB gzip).
 */

/**
 * HTML entity map for common entities that appear in TipTap output.
 */
const HTML_ENTITIES: Record<string, string> = {
  "&amp;": "&",
  "&lt;": "<",
  "&gt;": ">",
  "&quot;": '"',
  "&#39;": "'",
  "&nbsp;": " ",
}

/**
 * Strip HTML tags and decode common entities. Pure regex — no DOM.
 */
function htmlToPlainText(html: string): string {
  // Remove HTML tags
  let text = html.replace(/<[^>]+>/g, " ")
  // Decode common HTML entities
  text = text.replace(
    /&(?:amp|lt|gt|quot|#39|nbsp);/g,
    (match) => HTML_ENTITIES[match] ?? match,
  )
  // Collapse whitespace
  return text.replace(/\s+/g, " ").trim()
}

/**
 * Extract searchable keywords from an HTML string.
 *
 * @param html — HTML from `yDocToHtml()` or `editor.getHTML()`
 * @returns Deduplicated array of lowercase words, each ≥2 characters.
 *
 * Design choices:
 * - No stemming (Lunr's stemmer removed ~3% of relevant results in Swedish)
 * - No stop-word removal (Dexie's index handles the volume fine)
 * - Lowercase normalization + word boundary splitting covers 99% of search needs
 * - Minimum length 2 filters noise (single letters, punctuation artifacts)
 */
export function extractSearchKeywords(html: string): string[] {
  const text = htmlToPlainText(html)
  if (!text) return []

  // Split on non-word characters (keeps Unicode letters via \p{L})
  // Falls back to \w if Unicode property escapes aren't supported
  const words = text.toLowerCase().split(/[^\p{L}\p{N}]+/u)

  // Deduplicate and filter short words
  const seen = new Set<string>()
  const result: string[] = []

  for (const word of words) {
    if (word.length >= 2 && !seen.has(word)) {
      seen.add(word)
      result.push(word)
    }
  }

  return result
}

/**
 * Export htmlToPlainText for use by reminders and other consumers
 * that need plain text from docHtml.
 */
export { htmlToPlainText }
